import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { invalidateEffectiveQuotationQueries } from "@/features/quotation/hooks";
import { listStorePage } from "@/features/store/api";
import type { PageResponse, Store } from "@/features/store/types";
import {
  listUsers,
  listUserPage,
  createUser,
  updateUser,
  updateUserStoreAccess,
  deactivateUser,
  reactivateUser,
  deleteUser,
  resetUserPassword,
  changeOwnPassword,
  setOwnPin,
} from "./api";
import type {
  CreateUserPayload,
  UpdateUserPayload,
  UpdateStoreAccessPayload,
  ResetPasswordPayload,
  ChangeOwnPasswordPayload,
  SetOwnPinPayload,
  UserPageQuery,
} from "./types";

const QUERY_KEY = ["users"];
const USER_ACCESS_STORES_QUERY_KEY = [
  "stores",
  "user-access-lookup",
  { limit: 100 },
] as const;

export function useUsers() {
  return useQuery({
    queryKey: [...QUERY_KEY, "lookup", { limit: 100, offset: 0 }],
    queryFn: ({ signal }) => listUsers(signal),
  });
}

function normalizeUserPageQuery(query: UserPageQuery): UserPageQuery {
  return {
    limit: query.limit,
    offset: query.offset,
    search: query.search.trim(),
    role: query.role.trim(),
    active: query.active,
  };
}

export function useUserPage(query: UserPageQuery, enabled = true) {
  const normalizedQuery = normalizeUserPageQuery(query);
  const lastSuccessfulPage = useRef<
    Awaited<ReturnType<typeof listUserPage>> | undefined
  >(undefined);

  const result = useQuery({
    queryKey: [...QUERY_KEY, "page", normalizedQuery],
    queryFn: ({ signal }) => listUserPage(normalizedQuery, signal),
    enabled,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (result.isSuccess && !result.isPlaceholderData) {
      lastSuccessfulPage.current = result.data;
    }
  }, [result.data, result.isPlaceholderData, result.isSuccess]);

  if (result.isError && !result.data && lastSuccessfulPage.current) {
    return {
      ...result,
      data: lastSuccessfulPage.current,
      isPlaceholderData: true,
    };
  }

  return result;
}

async function listAllUserAccessStores(
  signal?: AbortSignal,
): Promise<PageResponse<Store>> {
  const limit = 100;
  const stores = new Map<string, Store>();
  let offset = 0;
  let expectedTotal: number | null = null;

  while (expectedTotal === null || stores.size < expectedTotal) {
    const page = await listStorePage({ limit, offset, search: "" }, signal);
    if (expectedTotal === null) {
      expectedTotal = Number.isFinite(page.total)
        ? Math.max(0, Math.floor(page.total))
        : page.items.length;
    }

    const sizeBeforePage = stores.size;
    for (const store of page.items) stores.set(store.id, store);
    if (
      page.items.length === 0 ||
      page.items.length < limit ||
      stores.size === sizeBeforePage
    ) {
      break;
    }
    offset += limit;
  }

  const items = [...stores.values()];
  return { items, total: items.length, limit, offset: 0 };
}

export function useUserAccessStores(enabled = false) {
  const queryClient = useQueryClient();
  const result = useQuery({
    queryKey: USER_ACCESS_STORES_QUERY_KEY,
    queryFn: ({ signal }) => listAllUserAccessStores(signal),
    enabled,
  });

  useEffect(() => {
    if (!enabled) {
      void queryClient.cancelQueries({
        queryKey: USER_ACCESS_STORES_QUERY_KEY,
        exact: true,
      });
    }
    return () => {
      void queryClient.cancelQueries({
        queryKey: USER_ACCESS_STORES_QUERY_KEY,
        exact: true,
      });
    };
  }, [enabled, queryClient]);

  return result;
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateUserPayload) => createUser(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateUserPayload }) =>
      updateUser(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateUserStoreAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateStoreAccessPayload;
    }) => updateUserStoreAccess(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["stores"] });
      invalidateEffectiveQuotationQueries(qc);
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["checkins"] });
    },
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deactivateUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reactivateUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useResetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: ResetPasswordPayload;
    }) => resetUserPassword(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useChangeOwnPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ChangeOwnPasswordPayload) =>
      changeOwnPassword(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["currentUser"] }),
  });
}

export function useSetOwnPin() {
  return useMutation({
    mutationFn: (payload: SetOwnPinPayload) => setOwnPin(payload),
  });
}
